import { ReactNode, useState } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  PageHeader,
  TPageHeaderProps,
} from '@/common/components/PageHeader/PageHeader';
import { tv } from '@/common/theme/tv.config';

export type TPageLayoutProps = {
  headerProps?: TPageHeaderProps;
  className?: string;
  children: React.ReactNode;
};

export function PageLayout({
  children,
  headerProps,
  className,
}: TPageLayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  return (
    <SafeAreaView className={styles.root} edges={['bottom']}>
      <KeyboardAvoidingView
        behavior="padding"
        className={styles.keyboardView({ scrolled: isScrolled })}
      >
        <ScrollView
          contentContainerClassName={styles.scrollViewContainer({
            withAction: !!headerProps?.primaryAction,
            className,
          })}
          // This is a workaround to prevent the scroll view from being transformed
          style={{ transform: 'none' }}
          onScroll={({ nativeEvent }) => {
            setIsScrolled(nativeEvent.contentOffset.y > 1);
          }}
          scrollEventThrottle={60}
        >
          {headerProps && <PageHeader {...headerProps} />}
          {children}
        </ScrollView>
        {headerProps?.primaryAction ? (
          <View className={styles.primaryButton}>
            {headerProps.primaryAction}
          </View>
        ) : null}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

export function PageLayoutHorizontalScroll({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <View className={styles.overflow}>
      <ScrollView
        horizontal
        contentContainerClassName={styles.overflowScroll({ className })}
        scrollEventThrottle={60}
        showsHorizontalScrollIndicator={false}
      >
        {children}
      </ScrollView>
    </View>
  );
}

const styles = {
  root: 'flex-1 bg-accentSecondary10 lg:bg-neutral20',
  keyboardView: tv({
    base: 'flex-1 border-t border-[transparent]',
    variants: {
      scrolled: {
        true: 'border-neutral20',
      },
    },
  }),
  scrollViewContainer: tv({
    base: 'gap-page mx-auto min-h-full w-full max-w-[80rem] p-lg pb-[8rem] pt-0 md:p-xl md:pb-[8rem] md:pt-0 lg:p-2xl lg:pt-2xl',
    variants: {
      withAction: {
        true: 'pb-[14.4rem] md:pb-[14.4rem] lg:pb-2xl',
      },
    },
  }),
  primaryButton: `${Platform.OS === 'web' ? 'fixed' : 'absolute'} bottom-[8rem] right-lg md:right-xl lg:right-2xl lg:hidden`,
  overflow: '-mx-lg md:-mx-xl lg:-mx-2xl -mb-xl',
  overflowScroll: tv({
    base: 'px-lg pb-xl',
  }),
} as const;
